// AnteprimaFiltro.tsx — v026: nello Store tocchi un filtro e lo provi su di te, con la fotocamera, prima di prenderlo.
// Lo vedi solo tu (non parte nessuna chiamata); in partita si usa solo dopo averlo preso. ‹ e › per passare agli
// altri filtri dell'elenco senza chiudere. v033: anche col dito (scorri a destra o a sinistra su tutto lo schermo) e
// con la striscia dei filtri vicini in basso (un tocco per provarne uno).
import { useEffect, useRef, useState } from "react";

import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useFiltri } from "../gioco/usaFiltri.ts";
import { apriFotocamera } from "../gioco/video.ts";
import { preparaRilevamento } from "../gioco/rilevamento.ts";
import { ConsensoVolto } from "../gioco/ConsensoVolto.tsx";
import { voltoAccettato } from "../servizi/account.ts";
import { IconaFiltro } from "../componenti/IconaFiltro.tsx";
import { vibra } from "../servizi/nativo.ts";
import { leggiPreferenza, salvaPreferenza } from "../servizi/preferenze.ts";
import { PREFERENZE } from "../costanti.ts";

/** Quanti pixel col dito per passare al filtro dopo (e il movimento deve essere più in orizzontale che in verticale). */
const SCORRI_PX = 45;
/** v042: sulla striscia dei filtri ogni tanti pixel di dito si passa a un filtro (si scorrono più filtri in un gesto). */
const PASSO_STRISCIA_PX = 56;
/** Filtri vicini nella striscia, per lato. */
const VICINI = 3;
/** Quello che serve di un tocco (evento del puntatore). */
type EventoPuntatore = { target: EventTarget; clientX: number; clientY: number };

export interface FiltroInAnteprima {
  id: string;
  nome: string;
  descrizione: string;
  prezzoCrediti: number;
  tuo: boolean;
}

interface Props {
  elenco: readonly FiltroInAnteprima[];
  inizio: number;
  /** Prendi il filtro mostrato (apre la conferma dell'acquisto); null = non si può (non connesso). */
  onPrendi: ((f: FiltroInAnteprima) => void) | null;
  onChiudi: () => void;
}

export function AnteprimaFiltro(p: Props) {
  const [volto, setVolto] = useState(voltoAccettato);
  if (!volto) return <ConsensoVolto onAccettato={() => setVolto(true)} onEsci={p.onChiudi} />;
  return <ConFotocamera {...p} />;
}

function ConFotocamera(p: Props) {
  const { t } = useTranslation();
  const [flusso, setFlusso] = useState<MediaStream | null>(null);
  const [errore, setErrore] = useState(false);
  useEffect(() => {
    let f: MediaStream | null = null;
    let chiuso = false;
    void preparaRilevamento();
    // v038: solo la fotocamera (niente microfono) e, se è ancora «occupata» dalla prova di prima (succede sui
    // telefoni: la fotocamera si libera un attimo dopo), si riprova qualche volta prima di dire che non va.
    const apri = async () => {
      for (let n = 0; ; n++) {
        try {
          return await apriFotocamera({ audio: false });
        } catch (e) {
          if (n >= 4 || e === "negato") throw e;
          await new Promise((ok) => setTimeout(ok, 400 * (n + 1)));
          if (chiuso) throw e;
        }
      }
    };
    apri()
      .then((x) => {
        if (chiuso) x.getTracks().forEach((tr) => tr.stop());
        else setFlusso((f = x));
      })
      .catch(() => !chiuso && setErrore(true));
    return () => {
      chiuso = true;
      f?.getTracks().forEach((tr) => tr.stop());
    };
  }, []);
  // v038: «indietro» di Android (e Esc) chiude la prova
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === "Escape" && p.onChiudi();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [p.onChiudi]);
  // sopra tutta la pagina (fuori dalla pagina dello Store, che dà ai suoi figli position: relative)
  return createPortal(
    <div className="ld-anteprima" role="dialog" aria-modal="true" aria-label={t("store.prova")}>
      {flusso ? (
        <Specchio {...p} flusso={flusso} />
      ) : (
        <div className="ld-anteprima-attesa">
          <p role={errore ? "alert" : "status"}>{t(errore ? "store.prova_errore" : "store.prova_attesa")}</p>
          <button type="button" className="pulsante" onClick={p.onChiudi}>
            {t("generale.chiudi")}
          </button>
        </div>
      )}
    </div>,
    document.body,
  );
}

function Specchio({ elenco, inizio, onPrendi, onChiudi, flusso }: Props & { flusso: MediaStream }) {
  const { t } = useTranslation();
  const filtri = useFiltri(flusso);
  const [i, setI] = useState(inizio);
  // v038: la parte in basso si riduce a una linea (e si riapre) per vedersi meglio; la scelta si ricorda
  const [ridotta, setRidotta] = useState(() => leggiPreferenza(PREFERENZE.provaRidotta) === "si");
  const riduci = (si: boolean) => {
    vibra("leggero");
    setRidotta(si);
    salvaPreferenza(PREFERENZE.provaRidotta, si ? "si" : "no");
  };
  const video = useRef<HTMLVideoElement>(null);
  const f = elenco[i] ?? null;
  const { motore, anteprima } = filtri;
  useEffect(() => {
    if (motore && f) anteprima(f.id);
  }, [motore, f, anteprima]);
  useEffect(() => {
    if (video.current) video.current.srcObject = motore && filtri.uscitaAttiva ? motore.uscita : flusso;
  }, [motore, filtri.uscitaAttiva, flusso]);
  const passa = (d: number) => {
    vibra("scelta");
    setI((x) => (((x + d) % elenco.length) + elenco.length) % elenco.length);
  };
  // v033: col dito. Si guarda solo dove si appoggia e dove si alza (niente trascinamento da seguire).
  // v042: anche sulla striscia dei filtri (prima il dito lì non faceva niente: sono pulsanti) — un gesto lungo passa
  // più filtri; dopo un gesto il tocco non vale come scelta.
  const tocco = useRef<{ x: number; y: number; striscia: boolean } | null>(null);
  const appenaScorso = useRef(0);
  const giu = (e: EventoPuntatore) => {
    const el = e.target as HTMLElement;
    const striscia = !!el.closest(".ld-anteprima-striscia");
    if (!striscia && el.closest("button, a")) return;
    tocco.current = { x: e.clientX, y: e.clientY, striscia };
  };
  const su = (e: EventoPuntatore) => {
    const t0 = tocco.current;
    tocco.current = null;
    if (!t0 || elenco.length < 2) return;
    const dx = e.clientX - t0.x;
    const dy = e.clientY - t0.y;
    if (Math.abs(dx) < SCORRI_PX || Math.abs(dx) <= Math.abs(dy) * 1.2) return;
    appenaScorso.current = Date.now();
    const passi = t0.striscia ? Math.max(1, Math.round(Math.abs(dx) / PASSO_STRISCIA_PX)) : 1;
    passa(dx < 0 ? passi : -passi);
  };
  const vicini = elenco.length > 1 ? Array.from({ length: Math.min(elenco.length, VICINI * 2 + 1) }, (_, k) => k - Math.min(VICINI, Math.floor((elenco.length - 1) / 2))) : [];
  return (
    <div className="ld-anteprima-superficie" onPointerDown={giu} onPointerUp={su} onPointerCancel={() => (tocco.current = null)}>
      <video ref={video} autoPlay playsInline muted className="video video-specchio ld-anteprima-video" />
      <div className="ld-anteprima-testa">
        <button type="button" className="pulsante-icona" aria-label={t("generale.chiudi")} onClick={onChiudi}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
        <span className="ld-anteprima-etichetta">{t("store.prova_etichetta")}</span>
      </div>
      {filtri.stato !== "pronto" && <p className="ld-anteprima-carica" role="status">{t("store.prova_attesa")}</p>}
      {f && ridotta && (
        <button type="button" className="ld-anteprima-linea" aria-expanded="false" aria-label={t("store.prova_apri")} onClick={() => riduci(false)}>
          <span className="ld-anteprima-linea-nome">{f.nome}</span>
          {/* v041: la freccia in su riapre i filtri */}
          <span className="ld-anteprima-freccia" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 15l6-6 6 6" /></svg>
          </span>
        </button>
      )}
      {f && !ridotta && (
        <div className="ld-anteprima-fondo">
          {/* v041: in alto a destra la freccia in giù riduce i filtri a una linea (prima: una maniglia che non si capiva) */}
          <button type="button" className="ld-anteprima-riduci ld-anteprima-freccia" aria-expanded="true" aria-label={t("store.prova_riduci")} title={t("store.prova_riduci")} onClick={() => riduci(true)}>
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
          </button>
          <div className="ld-anteprima-riga">
            {elenco.length > 1 && (
              <button type="button" className="pulsante-icona" aria-label={t("store.prova_prima")} onClick={() => passa(-1)}>
                ‹
              </button>
            )}
            <div className="ld-anteprima-testo" aria-live="polite">
              <IconaFiltro id={f.id} nome={f.nome} dimensione={64} classe="ld-anteprima-icona" />
              <strong>{f.nome}</strong>
              <span>{f.descrizione}</span>
            </div>
            {elenco.length > 1 && (
              <button type="button" className="pulsante-icona" aria-label={t("store.prova_dopo")} onClick={() => passa(1)}>
                ›
              </button>
            )}
          </div>
          {vicini.length > 0 && (
            <div className="ld-anteprima-striscia" role="listbox" aria-label={t("store.prova_altri")}>
              {vicini.map((d) => {
                const k = (i + d + elenco.length) % elenco.length;
                const g = elenco[k]!;
                return (
                  <button
                    key={`${g.id}-${d}`}
                    type="button"
                    role="option"
                    aria-selected={d === 0}
                    aria-label={g.nome}
                    className="ld-anteprima-vicino"
                    data-scelto={d === 0}
                    onClick={() => d !== 0 && Date.now() - appenaScorso.current > 400 && passa(d)}
                  >
                    <IconaFiltro id={g.id} nome={g.nome} dimensione={d === 0 ? 52 : 40} />
                  </button>
                );
              })}
            </div>
          )}
          {f.tuo ? (
            <p className="nota-centrata">{t("social.tuo")}</p>
          ) : onPrendi ? (
            <button type="button" className="ld-cta ld-cta-centrata" onClick={() => onPrendi(f)}>
              {t("social.prezzo_crediti", { n: f.prezzoCrediti })}
            </button>
          ) : null}
          <p className="nota-centrata">{t("store.prova_nota")}</p>
        </div>
      )}
    </div>
  );
}
